import { type Component, Show } from 'solid-js'
import { z } from 'zod'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Form from '~/components/Form'
import Input from '~/components/Input'
import Select from '~/components/Select'
import Switch from '~/components/Switch'
import { useToast } from '~/components/useToast'
import { useForm } from '~/hooks/useForm'
import { COMPANY_ROLE_OPTIONS } from '~/services/access.service'
import { type CompanyRoleType, type UserDetailType, userService } from '~/services/user.service'
import { resolveError } from '~/utils/error'

export interface UserProfileTabPropsType {
  /** La ficha del usuario cargada; null mientras llega. */
  user: () => UserDetailType | null | undefined
  /** El id del usuario editado. */
  userId: () => number
  /** Recarga la ficha tras guardar. */
  onRefetch: () => void
  /** true si el formulario es el usuario logueado. */
  isSelf: () => boolean
  /** true si quien edita es super administrador. */
  isRoot: () => boolean
  /** Si quien edita puede modificar usuarios. */
  canUpdate: boolean
  /** Si quien edita puede quitar usuarios de la empresa. */
  canDelete: boolean
  /** Pide confirmación y elimina al usuario de la empresa. */
  onDelete: () => void
}

const profileSchema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio'),
  email: z.string().min(1, 'El correo es obligatorio').email('Correo electrónico inválido'),
  document: z.string().optional(),
  // Vacía significa "no la cambies".
  password: z.string().refine(v => v.length === 0 || v.length >= 8, 'La contraseña debe tener al menos 8 caracteres'),
  status: z.boolean().default(true),
  companyRole: z.enum(['member', 'admin', 'owner']),
  superAdmin: z.boolean().default(false)
})

/** Pestaña de perfil y acceso: datos personales, rol en la empresa y estado de la cuenta. */
const UserProfileTab: Component<UserProfileTabPropsType> = props => {
  const toast = useToast()

  const profileForm = useForm({
    schema: profileSchema,
    initialValues: () => ({
      name: props.user()?.name ?? '',
      email: props.user()?.email ?? '',
      document: props.user()?.document ?? '',
      password: '',
      status: props.user()?.status !== 0,
      companyRole: props.user()?.companyRole ?? 'member',
      superAdmin: props.user()?.isRoot === true
    }),
    onSubmit: async values => {
      await userService.update(props.userId(), {
        name: values.name,
        email: values.email,
        document: values.document || undefined,
        ...(values.password ? { password: values.password } : {}),
        status: values.status ? 1 : 0,
        companyRole: values.companyRole as CompanyRoleType,
        // La marca de super admin sólo viaja si quien edita lo es.
        ...(props.isRoot() && values.superAdmin !== (props.user()?.isRoot === true) ? { isRoot: values.superAdmin } : {})
      })
      toast.addToast({ title: 'Usuario actualizado', type: 'success' })
      profileForm.setValue('password', '')
      props.onRefetch()
    },
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  return (
    <Card>
      <Card.Body>
        <Form id="user-profile-form" controller={profileForm}>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div class="space-y-4">
              <h3 class="text-sm font-semibold text-foreground">Datos personales</h3>
              <Input name="name" label="Nombre" />
              <Input name="email" label="Correo electrónico" type="email" />
              <Input name="document" label="Documento" />
            </div>

            <div class="space-y-4">
              <h3 class="text-sm font-semibold text-foreground">Acceso</h3>
              <Input name="password" label="Nueva contraseña" type="password" placeholder="Dejar vacío para no cambiarla" />
              <Select
                name="companyRole"
                label="Rol en la empresa activa"
                options={COMPANY_ROLE_OPTIONS}
                value={(profileForm.values().companyRole as string) ?? 'member'}
                disabled={props.isSelf() && props.user()?.companyRole === 'owner'}
              />
              <p class="text-xs text-foreground-subtle">El propietario se salta los permisos: puede todo dentro de su empresa.</p>

              <Switch name="status" label="Usuario activo" checked={Boolean(profileForm.values().status)} disabled={props.isSelf()} />
              <Show when={props.isSelf()}>
                <p class="text-xs text-foreground-subtle">No puedes desactivarte ni degradarte a ti mismo.</p>
              </Show>

              <Show when={props.isRoot()}>
                <div class="rounded-lg border border-danger-soft bg-danger-soft/20 p-3">
                  <Switch name="superAdmin" label="Super administrador" color="danger" checked={Boolean(profileForm.values().superAdmin)} disabled={props.isSelf()} />
                  <p class="mt-1 text-xs text-foreground-subtle">Acceso total al sistema y a todas las empresas, por encima de cualquier permiso.</p>
                </div>
              </Show>
            </div>
          </div>
        </Form>

        <div class="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <div class="text-xs text-foreground-subtle">
            <Show when={props.user()?.createdAt}>Creado el {new Date(props.user()!.createdAt!).toLocaleDateString()}. </Show>
            <Show when={props.user()?.googleLinked}>Cuenta de Google vinculada. </Show>
            <Show when={props.user()?.githubLinked}>Cuenta de GitHub vinculada.</Show>
          </div>
          <div class="flex gap-2">
            <Show when={props.canDelete && !props.isSelf()}>
              <Button type="button" variant="soft" color="danger" onClick={props.onDelete} prependIcon="material-symbols-light:delete-outline">
                Quitar de la empresa
              </Button>
            </Show>
            <Button type="submit" form="user-profile-form" disabled={profileForm.submitting() || !profileForm.valid() || !props.canUpdate}>
              {profileForm.submitting() ? 'Guardando...' : 'Guardar perfil'}
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

export default UserProfileTab
